import { useMemo, useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { Col, Flex, Grid, Input, Row, theme } from 'antd';
import { FiSearch } from 'react-icons/fi';
import { useSearchParams } from 'react-router';
import styled from 'styled-components';

import { useAuth } from '@contexts/User.context';
import { useToast } from '@contexts/Toast.context';
import {
	COLLECTION_SAVE_KEY,
	useCollections,
	useCreateCollection,
	useDeleteCollection,
} from '@hooks/useCollections';
import { useProjectTypeFilter } from '@hooks/useProjectTypeFilter';
import { filterObjectTree } from '@utils/taxonomyTree';
import { useObjects } from '@hooks/useObjects';
import { COLLECTION_TYPE } from '@utils/constants';

import View from '@components/layout/views/View';
import ProjectTypeFilter from '@components/filters/ProjectTypeFilter';

import BuilderPanel from './components/BuilderPanel';
import CollectionBuilder from './components/CollectionBuilder';
import CollectionDetails from './components/CollectionDetails';
import CollectionsList from './components/CollectionsList';
import DeleteCollectionModal from './components/DeleteCollectionModal';
import {
	filterAvailableNodes,
	indexByUuid,
	isSendable,
	matchesCollection,
	resolveObjects,
} from './collectionTree';
import { useCollectionBuilder } from './useCollectionBuilder';
import { useFillViewportHeight } from './useFillViewportHeight';

// A layout width, not a spacing step — the same right panel as the Objects page.
const PANEL_WIDTH = '340px';
const SELECTED_PARAM = 'collection';

// The Objects page's layout: the panel beside the list on wide screens, beneath
// it once the columns stack. `$wide` is the `lg` breakpoint.
const ListColumn = styled(Col)`
	${({ $wide, theme: { token } }) =>
		$wide && `height: 100%; min-width: 0; padding-inline-end: ${token.padding}px;`}
`;

const PanelColumn = styled(Col)`
	${({ $wide, theme: { token } }) =>
		$wide
			? `height: 100%;
				border-inline-start: ${token.lineWidth}px solid ${token.colorBorderSecondary};
				padding-inline-start: ${token.padding}px;`
			: `border-block-start: ${token.lineWidth}px solid ${token.colorBorderSecondary};
				padding-block-start: ${token.padding}px;`}
`;

// The list scrolls on its own on wide screens; the inset keeps cards off the scrollbar.
const ListScroll = styled.div`
	flex: 1;
	padding-inline-end: ${({ theme }) => theme.token.paddingXXS}px;
	${({ $wide }) => $wide && 'overflow-y: auto; min-height: 0;'}
`;

const Collections = () => {
	const { token } = theme.useToken();
	const { lg } = Grid.useBreakpoint();
	const [searchParams, setSearchParams] = useSearchParams();
	const [activeTab, setActiveTab] = useProjectTypeFilter();
	const { currentUser } = useAuth();
	const { addToast } = useToast();

	const builder = useCollectionBuilder();
	const { isBuilding, query } = builder;
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [copyingId, setCopyingId] = useState(null);

	const { data: collections = [], isLoading, isError } = useCollections();
	const taxonomy = useObjects(activeTab);
	const { mutate: createCollection } = useCreateCollection();
	const { mutate: deleteCollection, isPending: isDeleting } = useDeleteCollection();
	// Leaving the builder mid-save unmounts BuilderPanel, and TanStack then skips its
	// per-call callbacks: no toast, no selection, no error. So every exit waits.
	const isSaving = useIsMutating({ mutationKey: COLLECTION_SAVE_KEY }) > 0;

	// Leaving a project type drops the draft: its objects belong to the old tab's taxonomy.
	// Keyed on the tab itself, not the tab click, because Back and Forward change it too.
	const [draftTab, setDraftTab] = useState(activeTab);
	if (draftTab !== activeTab) {
		setDraftTab(activeTab);
		builder.close();
	}

	const [bodyRef, bodyHeight] = useFillViewportHeight(Boolean(lg));

	// The viewer's scope follows the active organization: its collections with one,
	// their personal ones without. Templates show in both; anything else the list
	// returns is filtered out below.
	//
	// TODO: tell organization members and admins apart once the app has an
	// authorization layer — until then both can edit (the Object Picker still
	// limits this to admins).
	const hasActiveOrg = Boolean(currentUser?.active_org_id);
	const ownedType = hasActiveOrg ? COLLECTION_TYPE.ORG : COLLECTION_TYPE.PERSONAL;
	const destination = hasActiveOrg ? 'Organization' : 'My collections';

	const objectIndex = useMemo(() => indexByUuid(taxonomy.tree), [taxonomy.tree]);

	const tabCollections = useMemo(
		() =>
			collections
				.filter(collection => collection.mode === activeTab)
				.map(collection => ({
					...collection,
					objects: resolveObjects(collection.objects, objectIndex),
				})),
		[collections, activeTab, objectIndex]
	);

	const trimmedQuery = query.trim();
	const listQuery = isBuilding ? '' : trimmedQuery.toLowerCase();
	const matched = tabCollections.filter(collection => matchesCollection(collection, listQuery));
	const ofType = type => matched.filter(collection => collection.type === type);

	const selectedId = searchParams.get(SELECTED_PARAM);
	const selectedCollection =
		tabCollections.find(
			collection => collection.id === selectedId && collection.type === ownedType
		) ?? null;

	const pickableTree = useMemo(() => filterAvailableNodes(taxonomy.tree), [taxonomy.tree]);
	const builderTree = useMemo(
		() => (isBuilding ? filterObjectTree(pickableTree, trimmedQuery) : []),
		[isBuilding, pickableTree, trimmedQuery]
	);
	const taxonomyReady = !taxonomy.isLoading && !taxonomy.isError;
	const editingCollection =
		tabCollections.find(collection => collection.id === builder.editingId) ?? null;
	// A kept object starts from the collection's own copy, so one missing from the
	// taxonomy keeps its label.
	const storedObjects = new Map(
		(editingCollection?.objects ?? []).map(object => [object.key, object])
	);
	const pickedObjects = resolveObjects(
		builder.pickedNodeUuids.map(key => storedObjects.get(key) ?? { key }),
		objectIndex
	);

	const selectCollection = id =>
		setSearchParams(
			previousParams => {
				const nextParams = new URLSearchParams(previousParams);
				if (id) nextParams.set(SELECTED_PARAM, id);
				else nextParams.delete(SELECTED_PARAM);
				return nextParams;
			},
			{ replace: true }
		);

	const showSuccess = (title, id, message) => addToast({ bg: 'success', title, message, id });
	const showError = (title, id, message = 'Please try again.') =>
		addToast({ bg: 'danger', title, message, id });

	// `name` is the one sent, not the response's: an empty response body still names it.
	const handleSaved = (saved, { created, name }) => {
		builder.close();
		if (saved?.id) selectCollection(saved.id);
		showSuccess(
			created
				? `Collection “${name}” created — it can be used for detection and annotation now`
				: `Collection “${name}” updated`,
			'collection-saved'
		);
	};

	// Objects outside this tab's taxonomy are left out of the copy, and the toast says so.
	const handleCopy = template => {
		const objects = template.objects.filter(object => isSendable(object, objectIndex));
		const leftOut = template.objects.length - objects.length;
		if (objects.length === 0) {
			showError(
				'Could not copy the template',
				'collection-copy-error',
				'None of its objects are in your taxonomy for this project type.'
			);
			return;
		}

		setCopyingId(template.id);
		createCollection(
			{ name: template.name, mode: activeTab, objects },
			{
				onSuccess: created => {
					if (created?.id) selectCollection(created.id);
					showSuccess(
						`“${template.name}” copied to ${destination}`,
						'collection-copied',
						leftOut
							? `${leftOut} of its objects ${leftOut === 1 ? 'is' : 'are'} not in your taxonomy and ${leftOut === 1 ? 'was' : 'were'} left out.`
							: undefined
					);
				},
				onError: () => showError('Could not copy the template', 'collection-copy-error'),
				onSettled: () => setCopyingId(null),
			}
		);
	};

	const handleDelete = collection =>
		deleteCollection(collection.id, {
			onSuccess: () => {
				if (collection.id === selectedId) selectCollection(null);
				setDeleteTarget(null);
				showSuccess('Collection deleted', 'collection-deleted');
			},
			onError: () => showError('Could not delete collection', 'collection-delete-error'),
		});

	const renderLeft = () =>
		isBuilding ? (
			<CollectionBuilder
				tree={builderTree}
				mode={activeTab}
				pickedNodeUuids={builder.pickedNodeUuids}
				onPick={builder.setPicked}
				isSearching={trimmedQuery !== ''}
				isEditing={builder.isEditing}
				isLoading={taxonomy.isLoading}
				isError={taxonomy.isError}
				onBack={builder.close}
				backDisabled={isSaving}
			/>
		) : (
			<CollectionsList
				templates={ofType(COLLECTION_TYPE.TEMPLATE)}
				owned={ofType(ownedType)}
				ownedHeading={hasActiveOrg ? 'ORGANIZATION' : 'MY COLLECTIONS'}
				ownedHint={hasActiveOrg ? 'shared across your organization' : undefined}
				isLoading={isLoading}
				isError={isError}
				isSearching={listQuery !== ''}
				copyLabel={`Copy to ${destination.toLowerCase()}`}
				copyingId={copyingId}
				// Until the taxonomy loads, objects without a stored uuid can't be given one.
				// One copy at a time: a second mutate() call would drop the first one's callbacks.
				copyDisabled={!taxonomyReady || copyingId !== null}
				selectedId={selectedCollection?.id}
				onNew={builder.openNew}
				onCopy={handleCopy}
				onSelect={collection => selectCollection(collection.id)}
				onEdit={builder.openEdit}
				onDelete={setDeleteTarget}
			/>
		);

	const renderRight = () =>
		isBuilding ? (
			<BuilderPanel
				key={builder.editingId ?? 'new'}
				builder={builder}
				pickedObjects={pickedObjects}
				mode={activeTab}
				savedHint={`Saved collections appear under ${destination}.`}
				onSaved={handleSaved}
				onSaveError={() => showError('Could not save collection', 'collection-save-error')}
			/>
		) : (
			<CollectionDetails collection={selectedCollection} onEdit={builder.openEdit} />
		);

	const searchLabel = isBuilding ? 'Search objects or aliases' : 'Search collections or objects';

	return (
		<View title={'Collections'} subtitle={'Reusable object sets for detection and annotation'}>
			<ProjectTypeFilter activeTab={activeTab} onChange={setActiveTab} disabled={isSaving} />

			<Row ref={bodyRef} gutter={[0, token.margin]} style={{ height: bodyHeight }}>
				<ListColumn xs={{ flex: '100%' }} lg={{ flex: '1 1 0' }} $wide={Boolean(lg)}>
					<Flex vertical gap="small" style={{ height: '100%', minHeight: 0 }}>
						<Input
							allowClear
							value={query}
							onChange={event => builder.setQuery(event.target.value)}
							placeholder={searchLabel}
							aria-label={searchLabel}
							prefix={<FiSearch aria-hidden color={token.colorTextTertiary} />}
						/>
						<ListScroll $wide={Boolean(lg)}>{renderLeft()}</ListScroll>
					</Flex>
				</ListColumn>

				<PanelColumn xs={{ flex: '100%' }} lg={{ flex: PANEL_WIDTH }} $wide={Boolean(lg)}>
					<Flex vertical gap="small" style={{ height: '100%', minHeight: 0 }}>
						{renderRight()}
					</Flex>
				</PanelColumn>
			</Row>

			<DeleteCollectionModal
				collection={deleteTarget}
				deleting={isDeleting}
				onConfirm={handleDelete}
				onCancel={() => setDeleteTarget(null)}
			/>
		</View>
	);
};

export default Collections;
